import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function Confidentialite() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-100 to-cream-200 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1.5 text-[13px] text-navy-500 hover:text-navy-800 mb-6">
          <ArrowLeft size={15} /> Retour à la connexion
        </Link>

        <div className="bg-white rounded-[22px] shadow-xl p-8 md:p-10">
          <h1 className="font-serif text-2xl text-navy-900 mb-1">Politique de confidentialité</h1>
          <p className="text-navy-300 text-sm mb-8">Protection des données personnelles — Hello Georges</p>

          <div className="space-y-6 text-[14px] leading-relaxed text-navy-700">
            <p>
              La protection des données des enfants, des familles et des équipes est une priorité.
              Le présent document décrit, conformément au Règlement Général sur la Protection des Données
              (RGPD) et à la loi Informatique et Libertés, la manière dont les données sont traitées dans
              l'application Hello Georges.
            </p>

            <section>
              <h2 className="font-semibold text-navy-900 mb-1.5">1. Responsable du traitement</h2>
              <p>
                L'éditrice, <strong>Marion Lumière</strong> (contact@hello-georges.com), est responsable de la
                plateforme. Lorsqu'une structure d'accueil utilise Hello Georges pour gérer ses propres données,
                cette structure est <strong>responsable de traitement</strong> et Hello Georges agit comme
                <strong> sous-traitant</strong> au sens de l'article 28 du RGPD (un contrat de sous-traitance
                encadre alors la relation).
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-navy-900 mb-1.5">2. Données collectées</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Comptes utilisateurs</strong> : nom, identifiant, mot de passe (chiffré/haché), rôle.</li>
                <li><strong>Enfants et familles</strong> : identité, coordonnées, informations d'accueil (présences, contrats), données de facturation.</li>
                <li><strong>Données de santé</strong> (le cas échéant) : PAI, vaccinations, informations médicales strictement nécessaires à l'accueil de l'enfant.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-semibold text-navy-900 mb-1.5">3. Finalités</h2>
              <p>
                Gestion administrative de la structure d'accueil : inscriptions, contrats, planning,
                facturation, transmissions quotidiennes, protocoles et suivi réglementaire (PMI, CAF).
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-navy-900 mb-1.5">4. Bases légales</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>Exécution du contrat d'accueil (art. 6.1.b du RGPD) ;</li>
                <li>Respect d'obligations légales — facturation, obligations PMI/CAF (art. 6.1.c) ;</li>
                <li>Pour les données de santé : nécessité liée à l'accueil de l'enfant, avec le consentement des responsables légaux lorsqu'il est requis (art. 9.2).</li>
              </ul>
            </section>

            <section>
              <h2 className="font-semibold text-navy-900 mb-1.5">5. Destinataires</h2>
              <p>
                Seul le personnel autorisé de la structure (accès restreint par rôles) accède aux données, ainsi
                que, le cas échéant, le comptable mandaté et les organismes légalement destinataires (PMI, CAF).
                <strong> Aucune donnée n'est vendue, louée ou utilisée à des fins publicitaires.</strong>
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-navy-900 mb-1.5">6. Hébergement et transferts</h2>
              <p>
                Les données sont hébergées par Infomaniak, en Suisse. La Suisse bénéficie d'une décision
                d'adéquation de la Commission européenne : elle offre un niveau de protection équivalent
                à celui du RGPD.
              </p>
              <p className="mt-2">
                L'application propose un <strong>assistant conversationnel (IA)</strong> fourni par le
                sous-traitant <strong>Anthropic, PBC (États-Unis)</strong>. Lorsqu'il est sollicité, les données
                qui lui sont transmises sont au préalable <strong>pseudonymisées</strong> : les noms des enfants,
                des familles et du personnel sont remplacés par des identifiants neutres (par exemple
                « Enfant 12 ») avant tout envoi, et aucun nom en clair ne quitte nos serveurs. Ce transfert hors
                Union européenne est encadré par les <strong>clauses contractuelles types</strong> de la Commission
                européenne. L'utilisation de cet assistant est facultative.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-navy-900 mb-1.5">7. Durée de conservation</h2>
              <p>
                Les données sont conservées pendant la durée de l'accueil de l'enfant, puis archivées ou
                supprimées selon les obligations légales applicables (par exemple, les pièces comptables sont
                conservées 10 ans). Les comptes utilisateurs sont désactivés puis supprimés à la fin de la
                relation.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-navy-900 mb-1.5">8. Sécurité</h2>
              <p>
                Connexion chiffrée (HTTPS), mots de passe hachés, accès restreint par rôles et sauvegardes
                régulières protègent les données contre les accès non autorisés.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-navy-900 mb-1.5">9. Cookies</h2>
              <p>
                L'application utilise uniquement un <strong>cookie technique de session</strong>, strictement
                nécessaire à votre connexion. Aucun cookie publicitaire ni de traçage n'est déposé : aucun
                bandeau de consentement n'est donc requis.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-navy-900 mb-1.5">10. Vos droits</h2>
              <p>
                Vous disposez d'un droit d'accès, de rectification, d'effacement, de limitation, d'opposition et
                de portabilité de vos données. Pour les exercer : <strong>contact@hello-georges.com</strong>.
                Vous pouvez également introduire une réclamation auprès de la CNIL (www.cnil.fr).
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-navy-900 mb-1.5">11. Registre des traitements</h2>
              <p>
                Conformément à l'article 30 du RGPD, un <strong>registre des activités de traitement</strong> est
                tenu à jour (finalités, catégories de données, destinataires, durées de conservation, mesures de
                sécurité). Il est consultable ici :{' '}
                <Link to="/registre-traitements" className="text-gold-600 hover:text-gold-700 underline">
                  registre des traitements
                </Link>.
              </p>
            </section>

            <p className="text-[12px] text-navy-300 pt-4 border-t border-cream-200">
              Dernière mise à jour : 22 juillet 2026.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
